import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Download, TrendingUp, Heart, Play, Users } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { API_BASE as API } from '../lib/apiBase';


const COLORS = ['#00D4FF', '#7000FF', '#FF4444', '#FFB800', '#00FF88', '#FF00FF'];

export default function AnalyticsPage() {
  const { t, lang } = useLang();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/analytics/overview`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const exportCSV = () => {
    window.open(`${API}/api/analytics/export?format=csv`, '_blank');
  };

  if (loading) return (
    <div className="min-h-screen pt-14 flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return null;

  return (
    <div data-testid="analytics-page" className="min-h-screen pt-14 pb-32">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading font-bold text-2xl">{t('analytics.title')}</h1>
          <button
            data-testid="export-csv-btn"
            onClick={exportCSV}
            className="flex items-center gap-2 bg-pulse-surface border border-pulse-border px-4 py-2 rounded-xl text-sm hover:border-cyan-500/30 transition-colors duration-200"
          >
            <Download size={14} /> {t('analytics.export')} CSV
          </button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-pulse-muted text-xs mb-1"><Play size={12} /> {t('analytics.total_plays')}</div>
            <p className="text-2xl font-heading font-bold text-pulse-primary">{data.total_plays?.toLocaleString()}</p>
          </div>
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-pulse-muted text-xs mb-1"><Heart size={12} /> {t('analytics.total_likes')}</div>
            <p className="text-2xl font-heading font-bold text-red-400">{data.total_likes?.toLocaleString()}</p>
          </div>
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-pulse-muted text-xs mb-1"><Users size={12} /> {t('dashboard.artists')}</div>
            <p className="text-2xl font-heading font-bold text-purple-400">{data.total_artists}</p>
          </div>
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-pulse-muted text-xs mb-1"><TrendingUp size={12} /> {t('dashboard.bookings')}</div>
            <p className="text-2xl font-heading font-bold text-green-400">{data.total_bookings}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Top Artists Chart */}
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
            <h3 className="font-heading font-semibold text-sm mb-4">{t('analytics.top_artists')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.top_artists}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="name" tick={{ fill: '#A1A1AA', fontSize: 11 }} />
                <YAxis tick={{ fill: '#A1A1AA', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#141417', border: '1px solid #27272A', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="plays" fill="#00D4FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Genre Distribution */}
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
            <h3 className="font-heading font-semibold text-sm mb-4">{t('analytics.genre_dist')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={data.genre_distribution}
                  dataKey="count"
                  nameKey="genre"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ genre, count }) => `${genre}: ${count}`}
                >
                  {data.genre_distribution?.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: '#141417', border: '1px solid #27272A', borderRadius: '8px', color: '#fff' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Songs Table */}
        <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
          <h3 className="font-heading font-semibold text-sm mb-4">{t('analytics.top_songs')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-pulse-muted text-xs border-b border-pulse-border">
                  <th className="pb-2 pr-4">#</th>
                  <th className="pb-2 pr-4">{lang === 'es' ? 'Cancion' : 'Song'}</th>
                  <th className="pb-2 pr-4">{lang === 'es' ? 'Artista' : 'Artist'}</th>
                  <th className="pb-2 pr-4 text-right">Plays</th>
                  <th className="pb-2 text-right">Likes</th>
                </tr>
              </thead>
              <tbody>
                {data.top_songs?.map((s, i) => (
                  <tr key={i} className="border-b border-pulse-border/50">
                    <td className="py-2 pr-4 text-pulse-muted">{i + 1}</td>
                    <td className="py-2 pr-4">{s.title_es}</td>
                    <td className="py-2 pr-4 text-pulse-muted">{s.artist_name}</td>
                    <td className="py-2 pr-4 text-right">{s.plays?.toLocaleString()}</td>
                    <td className="py-2 text-right">{s.likes?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
